(() => {
  const routesRoot = () => window.Shopify?.routes?.root || '/';

  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[character]);

  const formatMoney = (cents = 0) => {
    const settings = window.themeVariables?.settings || {};
    const format = settings.moneyWithCurrencyFormat || settings.moneyFormat || '';

    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(Number(cents) || 0, format);
    }

    return new Intl.NumberFormat(window.Shopify?.locale || document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
    }).format((Number(cents) || 0) / 100);
  };

  const parseIdList = (value = '') => [...new Set(
    String(value).split(',').map(Number).filter(Boolean)
  )];

  const parseIdSet = (value = '') => new Set(parseIdList(value));

  const availableVariants = (product) => (
    Array.isArray(product?.variants) ? product.variants.filter((variant) => variant.available !== false) : []
  );

  const productImageUrl = (product, variant = null) => {
    const image = variant?.featured_image || product?.featured_image || product?.featured_media || product?.images?.[0];
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  };

  const productType = (product) => {
    const identity = `${product?.title || ''} ${product?.handle || ''} ${product?.product_type || product?.type || ''}`.toLowerCase();
    if (identity.includes('mouth tape') || identity.includes('mouthtape') || identity.includes('mondtape')) return 'mouth';
    if (identity.includes('inhaler') || identity.includes('sniffit-stick') || identity.includes('thai blend') || identity.includes('thai-blend')) return 'inhaler';
    if (identity.includes('nasal strip') || identity.includes('neusstrip') || identity.includes('sniffit')) return 'nasal';
    return '';
  };

  const isThreeMonthItem = (item) => {
    const explicitValue = String(item?.properties?._zp01_three_month || '').toLowerCase();
    if (explicitValue === 'true') return true;
    if (explicitValue === 'false') return false;
    const planIdentity = `${item?.variant_title || ''} ${item?.selling_plan_allocation?.selling_plan?.name || ''}`.toLowerCase();
    if (planIdentity.includes('2+1')) return false;
    return Number(item?.properties?._zp01_pack_count) === 3
      || planIdentity.includes('3 month')
      || planIdentity.includes('3 maanden')
      || planIdentity.includes('3 maand');
  };

  const qualifiesByPlan = (item) => isThreeMonthItem(item) || Boolean(item?.selling_plan_allocation);

  const variantDisplayTitle = (product, variant, labels = {}) => {
    const title = String(variant?.title || '');
    const titleLower = title.toLowerCase();
    const productTitleLower = String(product?.title || '').toLowerCase();
    const unitLabel = productTitleLower.includes('mouth tape')
      || productTitleLower.includes('mouthtape')
      || productTitleLower.includes('mondtape')
      ? labels.nightUnit
      : productTitleLower.includes('nasal strip')
        || productTitleLower.includes('neusstrip')
        || productTitleLower.includes('sniffit')
        ? labels.stripUnit
        : '';

    if (!unitLabel) return title;

    const quantitySegment = title.replace(/[–—]/g, '-').split('-').pop()?.trim() || '';
    const measurementCount = Number(variant?.unit_price_measurement?.quantity_value) || 0;
    let unitCount = Number.parseInt(quantitySegment, 10) || measurementCount;
    let packCount = 1;

    if (titleLower.includes('2+1')) {
      packCount = 3;
      unitCount = measurementCount || unitCount;
      unitCount *= 3;
    } else if (titleLower.includes('month') || titleLower.includes('maand')) {
      packCount = Number.parseInt(titleLower, 10) || 1;
    }

    if (!unitCount) return title;
    const packLabel = packCount === 1 ? labels.packSingular : labels.packPlural;
    return `${packCount} ${packLabel} · ${unitCount} ${unitLabel}`;
  };

  class ZP02CartRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.dataset.ready = 'true';
        this.viewport = this.querySelector('[data-zp02-viewport]');
        this.scroller = this.querySelector('[data-zp02-scroller]');
        this.prevButton = this.querySelector('[data-zp02-prev]');
        this.nextButton = this.querySelector('[data-zp02-next]');
        this.dots = this.querySelector('[data-zp02-dots]');
        this.dragState = null;
        this.suppressClick = false;
        this.onScroll = () => {
          window.cancelAnimationFrame(this.navigationFrame);
          this.navigationFrame = window.requestAnimationFrame(() => this.updateNavigation());
        };
        this.onDrawerShow = () => requestAnimationFrame(() => this.updateNavigation());
        this.onPrevClick = () => this.scrollByCard(-1);
        this.onNextClick = () => this.scrollByCard(1);
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerEnd = this.handlePointerEnd.bind(this);
        this.onClickCapture = this.handleClickCapture.bind(this);
        this.onDragStart = (event) => event.preventDefault();
        this.resizeObserver = new ResizeObserver(this.onScroll);
        this.setupNavigation();
        this.loadRecommendations();
      }

      this.drawer = this.closest('cart-drawer');
      this.bindEvents();
      requestAnimationFrame(() => this.updateNavigation());
    }

    bindEvents() {
      if (this.eventsBound) return;
      this.eventsBound = true;
      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.viewport?.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport?.addEventListener('pointerdown', this.onPointerDown, true);
      this.viewport?.addEventListener('pointermove', this.onPointerMove, true);
      this.viewport?.addEventListener('pointerup', this.onPointerEnd, true);
      this.viewport?.addEventListener('pointercancel', this.onPointerEnd, true);
      this.viewport?.addEventListener('lostpointercapture', this.onPointerEnd);
      this.viewport?.addEventListener('click', this.onClickCapture, true);
      this.viewport?.addEventListener('dragstart', this.onDragStart);
      this.drawer?.addEventListener('dialog:after-show', this.onDrawerShow);
      if (this.viewport) this.resizeObserver.observe(this.viewport);
    }

    disconnectedCallback() {
      this.eventsBound = false;
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown, true);
      this.viewport?.removeEventListener('pointermove', this.onPointerMove, true);
      this.viewport?.removeEventListener('pointerup', this.onPointerEnd, true);
      this.viewport?.removeEventListener('pointercancel', this.onPointerEnd, true);
      this.viewport?.removeEventListener('lostpointercapture', this.onPointerEnd);
      this.viewport?.removeEventListener('click', this.onClickCapture, true);
      this.viewport?.removeEventListener('dragstart', this.onDragStart);
      this.drawer?.removeEventListener('dialog:after-show', this.onDrawerShow);
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.navigationFrame);
    }

    get cards() {
      return Array.from(this.scroller?.querySelectorAll('[data-zp02-card]') || []);
    }

    setupNavigation() {
      const cards = this.cards;
      this.classList.toggle('is-single', cards.length <= 1);
      if (this.dots) {
        this.dots.replaceChildren(...cards.map((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('aria-label', `${index + 1} / ${cards.length}`);
          dot.addEventListener('click', () => this.scrollToCard(index));
          if (index === 0) {
            dot.classList.add('is-active');
            dot.setAttribute('aria-current', 'true');
          }
          return dot;
        }));
      }
      if (this.prevButton) this.prevButton.disabled = true;
      if (this.nextButton) this.nextButton.disabled = cards.length <= 1;
      if (this.drawer?.open) requestAnimationFrame(() => this.updateNavigation());
    }

    scrollByCard(direction) {
      this.scrollToCard(this.activeCardIndex() + direction);
    }

    cardStep() {
      const firstCard = this.cards[0];
      if (!firstCard || !this.scroller) return 0;
      const gap = Number.parseFloat(getComputedStyle(this.scroller).columnGap) || 8;
      return firstCard.offsetWidth + gap;
    }

    activeCardIndex() {
      const step = this.cardStep();
      if (!step || !this.viewport) return 0;
      return Math.min(this.cards.length - 1, Math.max(0, Math.round(Math.abs(this.viewport.scrollLeft) / step)));
    }

    scrollToCard(index, behavior = 'smooth') {
      if (!this.viewport || this.cards.length === 0) return;
      const nextIndex = Math.min(this.cards.length - 1, Math.max(0, index));
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const logicalLeft = Math.min(maxScroll, nextIndex * this.cardStep());
      const left = document.documentElement.dir === 'rtl' ? -logicalLeft : logicalLeft;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.viewport.scrollTo({ left, behavior: reducedMotion ? 'auto' : behavior });
      window.setTimeout(() => this.updateNavigation(), reducedMotion ? 0 : 350);
    }

    handlePointerDown(event) {
      if (!this.viewport || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (event.target.closest('button, input, select, textarea, option, label')) return;

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScrollLeft: this.viewport.scrollLeft,
        dragging: false,
      };
    }

    handlePointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId || !this.viewport) return;
      const deltaX = event.clientX - this.dragState.startX;
      const deltaY = event.clientY - this.dragState.startY;

      if (!this.dragState.dragging) {
        if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.dragState = null;
          return;
        }
        this.dragState.dragging = true;
        this.suppressClick = true;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture?.(event.pointerId);
      }

      event.preventDefault();
      this.viewport.scrollLeft = this.dragState.startScrollLeft - deltaX;
      this.updateNavigation();
    }

    handlePointerEnd(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId || !this.viewport) return;
      const wasDragging = this.dragState.dragging;
      this.dragState = null;
      this.viewport.classList.remove('is-dragging');
      if (this.viewport.hasPointerCapture?.(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      if (wasDragging) {
        this.scrollToCard(this.activeCardIndex());
        window.setTimeout(() => { this.suppressClick = false; }, 0);
      }
    }

    handleClickCapture(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }

    updateNavigation() {
      if (!this.viewport) return;
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const isRtl = document.documentElement.dir === 'rtl';
      const normalizedScroll = Math.abs(this.viewport.scrollLeft);
      const hasOverflow = maxScroll > 2;
      if (this.prevButton) this.prevButton.disabled = normalizedScroll <= 2;
      if (this.nextButton) this.nextButton.disabled = this.cards.length <= 1 || (hasOverflow && normalizedScroll >= maxScroll - 2);

      const cards = this.cards;
      if (cards.length === 0 || !this.dots) return;
      const activeIndex = this.activeCardIndex();
      Array.from(this.dots.children).forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (isRtl) {
        if (this.prevButton) this.prevButton.disabled = hasOverflow && normalizedScroll >= maxScroll - 2;
        if (this.nextButton) this.nextButton.disabled = normalizedScroll <= 2;
      }
    }

    renderCard(product) {
      const variants = availableVariants(product);
      const preferPromoVariant = this.dataset.preferPromoVariant === 'true';
      const selectedVariant = preferPromoVariant
        ? variants.find((variant) => String(variant.title || '').toLowerCase().includes('2+1')) || variants[0]
        : variants[0];
      if (!selectedVariant) return '';

      const imageUrl = productImageUrl(product, selectedVariant);
      const hasOneVariant = variants.length === 1 || product.has_only_default_variant;
      const selectId = `zp02-json-variant-${product.id}`;
      const detailLabels = {
        packSingular: this.dataset.packSingular || 'bag',
        packPlural: this.dataset.packPlural || 'bags',
        stripUnit: this.dataset.stripUnit || 'strips',
        nightUnit: this.dataset.nightUnit || 'nights',
      };
      const variantControl = hasOneVariant
        ? `<input type="hidden" name="id" value="${selectedVariant.id}">`
        : `<label class="sr-only" for="${selectId}">Variant</label>
          <span class="zp02-routine-card__select-wrap">
            <select id="${selectId}" name="id" data-zp02-variant>
              ${variants.map((variant) => {
                const variantImage = productImageUrl(product, variant);
                const selected = Number(variant.id) === Number(selectedVariant.id) ? ' selected' : '';
                return `<option value="${variant.id}" data-price="${Number(variant.price) || 0}" data-compare-price="${Number(variant.compare_at_price) || 0}" data-image="${escapeHtml(variantImage)}"${selected}>${escapeHtml(variantDisplayTitle(product, variant, detailLabels))}</option>`;
              }).join('')}
            </select>
            <svg aria-hidden="true" focusable="false" width="10" viewBox="0 0 10 10"><path d="m1 3 4 4 4-4" fill="none" stroke="currentColor"/></svg>
          </span>`;
      const comparePrice = Number(selectedVariant.compare_at_price) || 0;
      const price = Number(selectedVariant.price) || 0;

      return `<article class="zp02-routine-card" data-zp02-card>
        <span class="zp02-routine-card__media">
          ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" loading="lazy" sizes="126px" class="zp02-routine-card__image" alt="${escapeHtml(product.title)}">` : ''}
        </span>
        <div class="zp02-routine-card__content">
          <span class="zp02-routine-card__title">${escapeHtml(product.title)}</span>
          <form method="post" action="${routesRoot()}cart/add" class="zp02-routine-card__form" data-zp02-form>
            ${variantControl}
            <input type="hidden" name="quantity" value="1">
            <input type="hidden" name="properties[_zp02_routine_offer]" value="true">
            <div class="zp02-routine-card__price" data-zp02-price-wrap>
              <s data-zp02-compare-price ${comparePrice <= price ? 'hidden' : ''}>${formatMoney(comparePrice)}</s>
              <strong data-zp02-price>${formatMoney(price)}</strong>
            </div>
            <button type="submit" class="zp02-routine-card__add" data-zp02-add>${escapeHtml(this.dataset.addLabel || 'Add')}</button>
          </form>
        </div>
      </article>`;
    }

    async loadRecommendations() {
      // Variant B renders its single upsell card server-side; Shopify recommendations must not replace it.
      if (this.dataset.zp04LockCards === 'true') return;

      const sourceProductIds = parseIdList(this.dataset.sourceProductIds).slice(0, 4);
      if (sourceProductIds.length === 0 || !this.scroller) return;

      const limit = Number(this.dataset.limit) || 5;
      const cartProductIds = parseIdSet(this.dataset.cartProductIds);
      const excludedProductIds = parseIdSet(this.dataset.excludedProductIds);
      const recommendedProductIds = new Set();
      const products = [];

      for (const sourceProductId of sourceProductIds) {
        if (products.length >= limit) break;

        try {
          const response = await fetch(
            `${routesRoot()}recommendations/products.json?product_id=${sourceProductId}&limit=10&intent=complementary`,
            { cache: 'no-store', headers: { Accept: 'application/json' } }
          );
          if (!response.ok) continue;

          const payload = await response.json();
          for (const product of Array.isArray(payload.products) ? payload.products : []) {
            const productId = Number(product.id);
            if (!productId
              || cartProductIds.has(productId)
              || excludedProductIds.has(productId)
              || recommendedProductIds.has(productId)
              || availableVariants(product).length === 0) continue;

            recommendedProductIds.add(productId);
            products.push(product);
            if (products.length >= limit) break;
          }
        } catch (error) {
          // Try the next source product; server-rendered fallbacks remain visible.
        }
      }

      const cards = products.map((product) => this.renderCard(product)).join('');
      if (!cards) return;

      this.scroller.innerHTML = cards;
      this.viewport.scrollLeft = 0;
      this.setupNavigation();
    }
  }

  if (!window.customElements.get('zp02-cart-recommendations')) {
    window.customElements.define('zp02-cart-recommendations', ZP02CartRecommendations);
  }

  const updateRecommendationVariant = (select) => {
    const option = select.options[select.selectedIndex];
    const card = select.closest('[data-zp02-card]');
    if (!option || !card) return;

    const price = Number(option.dataset.price) || 0;
    const comparePrice = Number(option.dataset.comparePrice) || 0;
    const priceElement = card.querySelector('[data-zp02-price]');
    const compareElement = card.querySelector('[data-zp02-compare-price]');
    const image = card.querySelector('.zp02-routine-card__image');
    if (priceElement) priceElement.textContent = formatMoney(price);
    if (compareElement) {
      compareElement.textContent = formatMoney(comparePrice);
      compareElement.hidden = comparePrice <= price;
    }
    if (image && option.dataset.image) image.src = option.dataset.image;
  };

  const addRecommendation = async (form, submitter) => {
    if (!form || submitter?.getAttribute('aria-busy') === 'true') return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const buttons = Array.from(form.elements).filter((element) => element.type === 'submit' || element.type === 'button');
    buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections },
      }));

      const formData = new FormData(form);
      formData.set('sections', [...new Set(sections)].join(','));
      if (formData.get('id')) formData.set('sections_url', `${routesRoot()}variants/${formData.get('id')}`);

      const response = await fetch(`${routesRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const payload = await response.json();

      if (!response.ok) {
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: payload.description || payload.message },
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      const cart = await (await fetch(`${routesRoot()}cart.js`, { cache: 'no-store' })).json();
      cart.sections = payload.sections || {};
      if ([...new Set(sections)].some((sectionId) => !cart.sections[sectionId])) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'zp02:recommendation-add', cart },
      }));
    } finally {
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  };

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-zp02-variant]');
    if (select) updateRecommendationVariant(select);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-zp02-form]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addRecommendation(form, event.submitter).catch((error) => console.error(error));
  }, true);

  class ZP02RewardGiftController {
    constructor(drawer) {
      this.drawer = drawer;
      this.pending = null;
      this.timer = null;
      this.enabled = drawer?.dataset.zp02RewardEnabled === 'true';
      this.complementaryThreshold = Number(drawer?.dataset.zp02ComplementaryThreshold) || 0;
      this.storageThreshold = Number(drawer?.dataset.zp02RewardThreshold) || 0;
      this.gifts = {
        mouth: {
          id: Number(drawer?.dataset.zp02MouthGiftVariantId) || 0,
          price: Number(drawer?.dataset.zp02MouthGiftVariantPrice) || 0,
          reward: 'mouth-tape',
          property: '_zp02_complementary_gift',
        },
        nasal: {
          id: Number(drawer?.dataset.zp02NasalGiftVariantId) || 0,
          price: Number(drawer?.dataset.zp02NasalGiftVariantPrice) || 0,
          reward: 'nasal-strips',
          property: '_zp02_complementary_gift',
        },
        storage: {
          id: Number(drawer?.dataset.zp02RewardVariantId) || 0,
          price: Number(drawer?.dataset.zp02RewardVariantPrice) || 0,
          reward: 'storage-box',
          property: '_zp02_threshold_gift',
        },
      };
      this.candidateVariantIds = parseIdSet(drawer?.dataset.zp02GiftCandidateIds);
      Object.values(this.gifts).forEach((gift) => {
        if (gift.id) this.candidateVariantIds.add(gift.id);
      });
    }

    property(item, key) {
      return item?.properties?.[key] == null ? '' : String(item.properties[key]);
    }

    qualifyingTotal(cart) {
      const itemTotal = cart.items.reduce((total, item) => {
        const isGift = this.property(item, '_zp01_gift') === 'true'
          || this.property(item, '_zp02_complementary_gift') === 'true'
          || this.property(item, '_zp02_threshold_gift') === 'true'
          || this.candidateVariantIds.has(Number(item.variant_id));
        return isGift ? total : total + Number(item.final_line_price || 0);
      }, 0);
      const cartDiscount = (cart.cart_level_discount_applications || [])
        .reduce((total, application) => total + Number(application.total_allocated_amount || 0), 0);
      return Math.max(0, itemTotal - cartDiscount);
    }

    paidItems(cart) {
      return cart.items.filter((item) => (
        this.property(item, '_zp01_gift') !== 'true'
        && this.property(item, '_zp02_complementary_gift') !== 'true'
        && this.property(item, '_zp02_threshold_gift') !== 'true'
        && !this.candidateVariantIds.has(Number(item.variant_id))
        && Number(item.final_line_price || 0) > 0
      ));
    }

    // Which gift the cart has earned the right to, regardless of pack size.
    cartProductTypes(cart) {
      return new Set(this.paidItems(cart).map((item) => productType({
        title: item.product_title || item.title,
        handle: item.handle || item.url,
        product_type: item.product_type,
      })).filter(Boolean));
    }

    // Whether the milestone is unlocked by plan (3-month or subscription).
    qualifyingProductTypes(cart) {
      return new Set(this.paidItems(cart).filter(qualifiesByPlan).map((item) => productType({
        title: item.product_title || item.title,
        handle: item.handle || item.url,
        product_type: item.product_type,
      })).filter(Boolean));
    }

    // Lines that unlock the gift on their own (by plan, or line total over the threshold).
    // Their gift is protected: adding a cheaper item must not swap it.
    anchorProductTypes(cart) {
      return new Set(this.paidItems(cart).filter((item) => (
        qualifiesByPlan(item)
        || (this.complementaryThreshold > 0 && Number(item.final_line_price || 0) >= this.complementaryThreshold)
      )).map((item) => productType({
        title: item.product_title || item.title,
        handle: item.handle || item.url,
        product_type: item.product_type,
      })).filter(Boolean));
    }

    desiredGifts(cart) {
      const qualifyingTotal = this.qualifyingTotal(cart);
      const desired = [];
      // Mirrors sections/cart-drawer.liquid — same keywords, same order. Keep both in step.
      // Which gift: a line that unlocks it on its own decides first; otherwise any pack size counts.
      // Whether it is unlocked: plan or spend.
      // Mouth tape and inhalers both earn nasal strips; nasal strips earn mouth tape.
      const cartTypes = this.cartProductTypes(cart);
      const qualifyingTypes = this.qualifyingProductTypes(cart);
      const anchorTypes = this.anchorProductTypes(cart);
      const hasNasal = cartTypes.has('nasal');
      const anchorEarnsNasal = anchorTypes.has('mouth') || anchorTypes.has('inhaler');
      const anchorEarnsMouth = anchorTypes.has('nasal');
      const unlockedByPlan = qualifyingTypes.size > 0;
      const unlockedBySpend = this.complementaryThreshold > 0 && qualifyingTotal >= this.complementaryThreshold;
      const earnsNasalGiftByPlan = qualifyingTypes.has('mouth') || qualifyingTypes.has('inhaler');

      const addGift = (gift) => {
        if (gift?.id && gift.price === 0 && !desired.some((candidate) => candidate.id === gift.id)) desired.push(gift);
      };

      if (earnsNasalGiftByPlan && qualifyingTypes.has('nasal')) {
        addGift(this.gifts.mouth);
        addGift(this.gifts.nasal);
      } else if (unlockedByPlan || unlockedBySpend) {
        if (anchorEarnsNasal && !anchorEarnsMouth) addGift(this.gifts.nasal);
        else if (anchorEarnsMouth && !anchorEarnsNasal) addGift(this.gifts.mouth);
        else if (hasNasal) addGift(this.gifts.mouth);
        else addGift(this.gifts.nasal);
      }

      if ((earnsNasalGiftByPlan && qualifyingTypes.has('nasal'))
        || (this.storageThreshold > 0 && qualifyingTotal >= this.storageThreshold)) addGift(this.gifts.storage);

      return desired;
    }

    schedule(cart = null) {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        this.reconcile(cart).catch((error) => console.error(error));
      }, 0);
    }

    async sections() {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections },
      }));
      return [...new Set(sections)];
    }

    async dispatchCart(cart, baseEvent) {
      const hasSections = cart.sections && Object.keys(cart.sections).length > 0;
      if (!hasSections) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent, cart },
      }));
    }

    async updateLines(updates) {
      const sections = await this.sections();
      const response = await fetch(`${routesRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates, sections: sections.join(',') }),
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Reward gift update failed');
      await this.dispatchCart(cart, 'zp02:reward-reconcile');
      return cart;
    }

    async addGifts(gifts) {
      const sections = await this.sections();
      const response = await fetch(`${routesRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: gifts.map((gift) => ({
            id: gift.id,
            quantity: 1,
            properties: { [gift.property]: 'true', _zp02_reward: gift.reward },
          })),
          sections: sections.join(','),
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'Reward gift add failed');
      const cart = await (await fetch(`${routesRoot()}cart.js`, { cache: 'no-store' })).json();
      cart.sections = payload.sections || {};
      await this.dispatchCart(cart, 'zp02:reward-reconcile');
      return cart;
    }

    async reconcile(cart = null) {
      if (!this.enabled) return cart;
      if (this.pending) return this.pending;

      this.pending = (async () => {
        let currentCart = cart || await (await fetch(`${routesRoot()}cart.js`, { cache: 'no-store' })).json();
        const desired = this.desiredGifts(currentCart);
        const desiredByReward = new Map(desired.map((gift) => [gift.reward, gift]));
        const keptRewards = new Set();
        const updates = {};

        currentCart.items.forEach((item) => {
          const isManaged = this.property(item, '_zp02_complementary_gift') === 'true'
            || this.property(item, '_zp02_threshold_gift') === 'true';
          if (!isManaged) return;

          const reward = this.property(item, '_zp02_reward')
            || (this.property(item, '_zp02_threshold_gift') === 'true' ? 'storage-box' : '');
          const desiredGift = desiredByReward.get(reward);
          const matchesDesired = desiredGift && Number(item.variant_id) === desiredGift.id;

          if (!matchesDesired || keptRewards.has(reward)) {
            updates[item.key] = 0;
            return;
          }

          keptRewards.add(reward);
          if (Number(item.quantity) !== 1) updates[item.key] = 1;
        });

        if (Object.keys(updates).length > 0) currentCart = await this.updateLines(updates);

        const giftsToAdd = desired.filter((gift) => !currentCart.items.some(
          (item) => Number(item.variant_id) === gift.id
        ));
        if (giftsToAdd.length > 0) currentCart = await this.addGifts(giftsToAdd);

        return currentCart;
      })().finally(() => { this.pending = null; });

      return this.pending;
    }

    install() {
      if (!this.enabled) return;
      document.addEventListener('cart:change', (event) => {
        if (event.detail?.baseEvent !== 'zp02:reward-reconcile') this.schedule(event.detail?.cart);
      });
      document.addEventListener('cart:refresh', () => this.schedule());
      window.addEventListener('pageshow', () => this.schedule());
      this.schedule();
    }
  }

  const installRewardController = () => {
    const drawer = document.querySelector('cart-drawer[data-zp02-reward-enabled]');
    if (!drawer || window.ZP02RewardGiftController) return;
    window.ZP02RewardGiftController = new ZP02RewardGiftController(drawer);
    window.ZP02RewardGiftController.install();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installRewardController, { once: true });
  } else {
    installRewardController();
  }
})();
